import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteTag } from 'api/tags';
import { queryKeys } from 'constants/queryKeys';
import { tagManagementStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import { issueMessage } from 'utils/scenarioIssues';

type UseDeleteTagOptions = {
  onSuccess?: () => void;
};

type UseDeleteTagResult = {
  remove: (tagId: number) => void;
  isDeleting: boolean;
  error: string | undefined;
};

export function useDeleteTag(
  options: UseDeleteTagOptions = {},
): UseDeleteTagResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [error, setError] = useState<string | undefined>();

  const mutation = useMutation({
    mutationFn: async (tagId: number) => {
      if (!user) {
        throw new Error(tagManagementStrings.errors.unauthorized);
      }
      return deleteTag(tagId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.TAGS, user?.uid],
      });
      // 削除でタグの付与も外れるため、友だち一覧/詳細のチップを再取得する。
      queryClient.invalidateQueries({
        queryKey: [queryKeys.LINE_FRIENDS, user?.uid],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.LINE_FRIEND_DETAIL],
      });
      setError(undefined);
      options.onSuccess?.();
    },
    onError: (caught) => {
      setError(issueMessage(caught, tagManagementStrings.errors.deleteFailed));
    },
  });

  const remove = (tagId: number) => {
    setError(undefined);
    mutation.mutate(tagId);
  };

  return {
    remove,
    isDeleting: mutation.isPending,
    error,
  };
}
